import React from "react";
import styles from "./Veille95Fond.module.css";
import {resoudre} from "../reglages";
import {REGLAGES} from "./reglages";
import {quantites} from "./densite";
import {sommets, points, valeursAnimation} from "./formes";

/*
 * Fond « Écran de veille 95 » : trois écrans de veille dans l'esprit de ceux de Windows 95 et de Windows Vista, sur fond noir ou bleu nuit.
 *   - Mystify : des polygones aux sommets qui rebondissent, avec leur traînée et des couleurs qui changent ;
 *   - ciel d'étoiles : des points de couleurs variées qui foncent vers l'écran depuis le centre ;
 *   - bulles : des bulles de savon translucides qui montent en ondulant (l'écran « Bulles » de Windows Vista).
 * Dessin et code originaux (aucun logo, image ni élément de Microsoft ; seule l'idée des écrans vient de Windows). Mystify est fait d'animations SVG déclaratives
 * (SMIL, valeurs calculées une fois par formes.js, boucle qui se referme) ; les deux autres, de CSS (transform).
 * Réglages (reglages.js) : écran (dont « au hasard »), vitesse, densité (traduite en quantités par densite.js).
 */

// couleurs de Mystify quand elles changent : le cycle se referme sur la première
const CYCLE = ["#ff0000", "#ffff00", "#00ff00", "#00ffff", "#0000ff", "#ff00ff", "#ff0000"];
// teintes des bulles et des étoiles (rgb) : reflets de savon, un peu de tout
const TEINTES = ["80, 200, 255", "255, 120, 220", "255, 230, 100", "120, 255, 170", "170, 140, 255"];
const ECRANS = ["mystify", "etoiles", "bulles"];

// Mystify est dessiné dans un repère fixe de 1 000 × 112, étiré à la largeur du bandeau (traits d'épaisseur constante)
const LARGEUR = 1000, HAUTEUR = 112, MARGE = 4;
const DUREE_BOUCLE = 48;    // secondes, à la vitesse ×1
const ECART_TRAINEE = 0.3;  // secondes (à ×1) entre deux images de la traînée

/** Générateur pseudo-aléatoire à graine (mulberry32) : mêmes étoiles à chaque affichage. */
function aleatoire(graine) {
    let a = graine >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Cycle de couleurs qui part de la couleur n° `depart` et se referme sur elle (chaque polygone a un départ différent). */
function cycleDepuis(depart) {
    const base = CYCLE.slice(0, 6), k = depart % 6;
    const tournee = base.slice(k).concat(base.slice(0, k));
    return tournee.concat(tournee[0]).join(";");
}

const mouvementReduit = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Mystify({formes, traine, vitesse, anime}) {
    const duree = DUREE_BOUCLE / vitesse;
    return (
        <svg className={styles.svg} viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`} preserveAspectRatio="none" shapeRendering="crispEdges">
            {Array.from({length: formes}, (_, f) => {
                const forme = sommets(f + 1);
                const valeurs = valeursAnimation(forme, LARGEUR, HAUTEUR, MARGE);
                return Array.from({length: traine}, (_, c) => {
                    const decalage = `${(c * ECART_TRAINEE / vitesse).toFixed(2)}s`;
                    return (
                        <polygon
                            key={f + "-" + c}
                            points={points(forme, 0, LARGEUR, HAUTEUR, MARGE)}
                            fill="none"
                            stroke={CYCLE[(f * 2) % 6]}
                            strokeWidth="2"
                            vectorEffect="non-scaling-stroke"
                        >
                            {anime && <animate attributeName="points" dur={`${duree}s`} begin={decalage} repeatCount="indefinite" values={valeurs}/>}
                            {anime && (
                                <animate attributeName="stroke" dur={`${duree / 2}s`} begin={decalage} repeatCount="indefinite" values={cycleDepuis(f * 2)}/>
                            )}
                        </polygon>
                    );
                });
            })}
        </svg>
    );
}

function Etoiles({nombre, vitesse}) {
    return Array.from({length: nombre}, (_, i) => {
        const r = aleatoire(7001 + i * 104729);
        const angle = r() * Math.PI * 2, duree = (3 + r() * 3) / vitesse, taille = 2 + Math.floor(r() * 3);
        const teinte = TEINTES[Math.floor(r() * TEINTES.length)];
        return (
            <span
                key={i}
                className={styles.etoile}
                style={{
                    width: taille,
                    height: taille,
                    background: `rgb(${teinte})`,
                    "--dx": (Math.cos(angle) * 55).toFixed(1),
                    "--dy": (Math.sin(angle) * 110).toFixed(1),
                    animationDuration: `${duree.toFixed(2)}s`,
                    animationDelay: `${(-r() * duree).toFixed(2)}s`,
                }}
            />
        );
    });
}

/** Bulles de savon : chacune monte d'une seule animation (transform), son ondulation est portée par une enveloppe intérieure. */
function Bulles({nombre, vitesse}) {
    return Array.from({length: nombre}, (_, i) => {
        const r = aleatoire(31000 + i * 15485863);
        const taille = 18 + Math.floor(r() * 62), duree = (7 + r() * 8) / vitesse, ondulation = 10 + r() * 30;
        const teinte = TEINTES[Math.floor(r() * TEINTES.length)];
        return (
            <span
                key={i}
                className={styles.bulleMonte}
                style={{
                    left: `${(((i + r() * 0.8) / nombre) * 100).toFixed(1)}%`,
                    animationDuration: `${duree.toFixed(2)}s`,
                    animationDelay: `${(-r() * duree).toFixed(2)}s`,
                }}
            >
                <span
                    className={styles.bulle}
                    style={{
                        width: taille,
                        height: taille,
                        "--teinte": teinte,
                        "--ondulation": `${ondulation.toFixed(0)}px`,
                        animationDuration: `${((2.5 + r() * 3) / vitesse).toFixed(2)}s`,
                        animationDelay: `${(-r() * 3).toFixed(2)}s`,
                    }}
                />
            </span>
        );
    });
}

export default function Veille95Fond({reglages}) {
    const {ecran: choisi, vitesse, densite} = resoudre(REGLAGES, reglages);
    // « au hasard » : l'écran est tiré une fois, quand le fond apparaît (le bandeau remonte le fond à chaque changement de fond)
    const [tire] = React.useState(() => ECRANS[Math.floor(Math.random() * ECRANS.length)]);
    const ecran = choisi === "aleatoire" ? tire : choisi;
    const {formes, traine, etoiles, bulles} = quantites(densite);
    return (
        <div className={`${styles.scene} ${ecran === "bulles" ? styles.sceneBulles : ""}`} aria-hidden="true">
            {ecran === "mystify" && <Mystify formes={formes} traine={traine} vitesse={vitesse} anime={!mouvementReduit()}/>}
            {ecran === "etoiles" && <div className={styles.ciel}><Etoiles nombre={etoiles} vitesse={vitesse}/></div>}
            {ecran === "bulles" && <Bulles nombre={bulles} vitesse={vitesse}/>}
        </div>
    );
}
